// ── Tipos del motor de Evo Brain ──────────────────────────────────────────────
// La lógica de conversación/streaming/SSE vivía acá (hook useAssistant, por
// página) — se movió a contexts/AssistantContext.tsx (2026-08-05, "el
// asistente se pierde al navegar") para sobrevivir a cambios de ruta. Este
// archivo queda solo con los tipos compartidos (AssistantContext.tsx y
// AssistantDock.tsx los importan) y las dos queries de aprobaciones
// permanentes, que no tienen relación con el motor de conversación.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantMessage {
  id: number;
  role: string;       // 'user' | 'assistant' | 'tool_call' | 'tool_result' | 'tool_proposal'
  content: string;
  toolName: string | null;
  toolStatus: string | null;
  isUndone: number;
  humanSummary: string | null;
  createdAt: string;
  undoable: boolean;
  // Presente solo cuando role === 'tool_proposal' (Evo Brain propose-then-approve)
  proposal?: {
    taskId: string;
    turnGroupId: string;
    verb: ProposalVerb;
    summary: ProposalSummary;
    status: 'pending' | 'approved' | 'rejected';
  } | null;
}

export type ProposalVerb = 'create' | 'edit' | 'send' | 'money' | 'delete' | 'publish';
export interface ProposalChange { field: string; from: string | null; to: string }
export interface ProposalSummary {
  sentence: string;
  changes?: ProposalChange[];
  irreversible?: boolean;
  warning?: string;
}

export interface AssistantPill {
  toolName: string;
  label: string;
  requiresInput: boolean;
}

export type StreamingPhase = 'idle' | 'thinking' | 'tool_executing' | 'text';

export interface StreamingState {
  phase: StreamingPhase;
  toolLabel?: string;
  toolName?: string;
  textAccum?: string;  // accumulated text delta during streaming
}

// ── Aprobaciones permanentes (Settings → Evo Brain) ──────────────────────────

export interface ToolApproval {
  id: number;
  toolName: string;
  /** Tipo de pedido eximido. '' = la herramienta entera (capa 3). */
  scope: string;
  label: string;
  grantedAt: string;
}

export function useToolApprovals() {
  return useQuery<{ approvals: ToolApproval[] }>({
    queryKey: ['assistant-tool-approvals'],
    queryFn: () => api.get('/api/assistant/tool-approvals'),
    staleTime: 10_000,
  });
}

export function useRevokeToolApproval() {
  const qc = useQueryClient();
  return useMutation({
    // Sin scope revoca la herramienta entera; con scope, solo ese tipo de
    // pedido — una misma herramienta puede tener varias exenciones (capa 3).
    mutationFn: ({ toolName, scope }: { toolName: string; scope?: string }) =>
      api.delete(`/api/assistant/tool-approvals/${toolName}${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['assistant-tool-approvals'] }); },
  });
}
